import { type ResultOf } from '@graphql-typed-document-node/core'
import { type TFunction } from 'i18next'
import * as R from 'remeda'

import { graphql } from '~/gql'

import { applyOr, findByPath } from '../generals'

import { conditionOperationLabelI18nKeyMap } from './conditionOperation'
import { matchSurveyFilter } from './matchSurveyFilter'
import { CustomerAttributeOp, QuestionOp } from './modelOperators'
import { formatNineSegment } from './segmentHelpers'
import { type NineNumber } from './segments'
import { type SurveyFilterItem } from './surveyFilterGroups'

const questionFragment = graphql(`
  fragment SurveyFilterToText_Question on Question {
    question_id
    question_text
  }
`)
type Question = ResultOf<typeof questionFragment>

const customerAttributeGroupFragment = graphql(`
  fragment SurveyFilterToText_CustomerAttributeGroup on CustomerAttributeGroup {
    id
    name
  }
`)
type CustomerAttributeGroup = ResultOf<typeof customerAttributeGroupFragment>

const brandFragment = graphql(`
  fragment SurveyFilterToText_Brand on Brand {
    id
    name
    number
  }
`)
type Brand = ResultOf<typeof brandFragment>

const brandGroupFragment = graphql(`
  fragment SurveyFilterToText_BrandGroup on BrandGroup {
    id
    name
    brands {
      ...SurveyFilterToText_Brand
    }
  }
`)
type BrandGroup = ResultOf<typeof brandGroupFragment>

/**
 * TODO: [i18n改善] t関数を引数で受け取る設計を見直す
 * 翻訳キーを返し、呼び出し側でt()を適用する形に変更することで、保守性の改善を行う
 */
type SurveyFilterToTextParams = {
  filter: SurveyFilterItem
  questions: Question[]
  customerAttributeGroups: CustomerAttributeGroup[]
  brandGroups: BrandGroup[]
  t: TFunction
}

/**
 * サーベイフィルターをテキストに変換します。
 *
 * @param params - 変換に必要なパラメーター。
 * @returns テキスト。
 */
export function surveyFilterToText(params: SurveyFilterToTextParams) {
  const { brandGroups, customerAttributeGroups, filter, questions, t } = params

  return matchSurveyFilter(filter, {
    question: (question) => {
      const questionText = R.pipe(
        questions,
        findByPath('question_id', question.questionId),
        applyOr(QuestionOp.toText, t('unknown_questionnaire_3')),
      )
      const answerOptionTexts = question.answerOptions.map(R.prop('text'))

      return [
        questionText,
        ...answerOptionTexts,
        t(conditionOperationLabelI18nKeyMap[question.operation]),
      ].join(' ')
    },
    customerAttribute: (customerAttribute) => {
      const names = customerAttribute.customerAttributeIds.map(
        (customerAttributeId) =>
          CustomerAttributeOp.getNameFromGroups(
            customerAttributeGroups,
            customerAttributeId,
            t,
          ),
      )
      const customerAttributeGroupText = names[0]?.customerAttributeGroupName
      const customerAttributeTexts = names.map(R.prop('customerAttributeName'))

      return [
        t('customized_segments_group_6'),
        customerAttributeGroupText,
        ...customerAttributeTexts,
        t(conditionOperationLabelI18nKeyMap[customerAttribute.operation]),
      ].join(' ')
    },
    brandSegments: (brandSegments) => {
      const brandGroup = findByPath(
        brandGroups,
        'id',
        brandSegments.brandGroupId,
      )
      const brand = findByPath(
        (brandGroup?.brands ?? []) as Brand[],
        'number',
        brandSegments.brandNumber,
      )
      const nineSegmentTexts = brandSegments.segmentNumbers
        .map((nineNumber) =>
          formatNineSegment(nineNumber as NineNumber, '%t', t),
        )
        .join(',')

      return [`[${brandGroup?.name}]`, brand?.name, nineSegmentTexts].join(' ')
    },
  })
}
